import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useConductBoard, useOpenGrievanceChannel } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

const GRIEVANCE_CLS: Record<string, string> = {
  Open: "bg-amber-50 text-status-amber",
  Investigated: "bg-blue-50 text-brand-primary",
  Resolved: "bg-green-50 text-status-green",
  Invalid: "bg-brand-grey-light text-brand-grey",
};

const OUTCOME_CLS: Record<string, string> = {
  Upheld: "bg-red-50 text-status-red",
  "Not upheld": "bg-green-50 text-status-green",
  Withdrawn: "bg-brand-grey-light text-brand-grey",
};

/** Conduct (WP-24 wave U, HRX-11): concerns raised, and cases opened.
 *
 * `Employee Grievance` and `Grievance Type` both held zero rows. On 105 employees across 210 departments
 * nobody could file a concern at all — and not merely because nobody had: `grievance_type` is mandatory and
 * there were no types to choose from, so the channel was unusable rather than unused.
 *
 * The two registers sit on one screen because one person handles both, but they are **not the same record**.
 * A grievance is a protected complaint raised by an employee; a disciplinary case is a sanction raised against
 * one, and different rights attach — a right of reply, a ladder of escalating warnings, an appeal. hrms ships
 * only the first, so the second is a OneSphere record with its own guards. */
export default function Conduct({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useConductBoard(company || undefined);
  const openChannel = useOpenGrievanceChannel();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-conduct>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-conduct-strip>
        <Stat label={t("cdt.grievances")} value={d.grievance_summary.grievances}
          hint={t("cdt.grievancesHint", { open: d.grievance_summary.open })} />
        <Stat label={t("cdt.overdue")} value={d.grievance_summary.overdue}
          cls={d.grievance_summary.overdue ? "text-status-amber" : "text-status-green"}
          hint={t("cdt.overdueHint", { days: d.sla_days })} />
        <Stat label={t("cdt.cases")} value={d.case_summary.cases}
          hint={t("cdt.casesHint", { open: d.case_summary.open })} />
        <Stat label={t("cdt.badlyRun")} value={d.case_summary.with_faults}
          cls={d.case_summary.with_faults ? "text-status-red" : "text-status-green"}
          hint={t("cdt.badlyRunHint")} />
        <Stat label={t("cdt.retention")} value={t("cdt.daysN", { n: d.retention_days })}
          hint={t("cdt.retentionHint")} />
      </div>

      {failing.length > 0 && (
        <SectionCard title={t("cdt.findings")} aside={t("cdt.findingsN", { n: failing.length, of: d.state.length })}>
          <ul>
            {failing.map((s) => (
              <li key={s.key} className="flex flex-wrap items-start justify-between gap-3 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
                data-conduct-finding={s.key}>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
                  <div>
                    <div className="font-semibold">{t(`cdt.check.${s.key}.title`)}</div>
                    <p className="mt-0.5 max-w-3xl text-sm text-brand-grey">{t(`cdt.check.${s.key}.bad`, s.facts) as string}</p>
                  </div>
                </div>
                {d.permissions.manage && s.key === "grievance_channel" && (
                  <button onClick={() => openChannel.mutate()} disabled={openChannel.isPending}
                    data-conduct-open-channel
                    className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {t("cdt.openChannel")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <SectionCard title={t("cdt.grievanceRegister")}
        aside={t("cdt.resolvedIn", { days: d.grievance_summary.average_days_to_resolve })}>
        {d.grievances.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-conduct-grievances-empty>{t("cdt.noGrievances")}</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {d.grievances.map((g) => (
                <tr key={g.name} className="border-t border-brand-grey-light first:border-t-0" data-conduct-grievance={g.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{g.subject}</div>
                    <div className="text-[11px] text-brand-grey">
                      {g.employee_name} · {g.grievance_type} · {g.date}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${GRIEVANCE_CLS[g.status ?? ""] ?? ""}`}>
                      {g.status}
                    </span>
                    <div className={`text-[11px] ${g.overdue ? "text-status-amber" : "text-brand-grey"}`}>
                      {g.overdue ? t("cdt.waitingDays", { n: g.days }) : t("cdt.daysN", { n: g.days })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={t("cdt.caseRegister")} aside={t("cdt.ladder", { list: d.ladder.join(" → ") })}>
        {d.cases.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-conduct-cases-empty>{t("cdt.noCases")}</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {d.cases.map((c) => (
                <tr key={c.name} className="border-t border-brand-grey-light first:border-t-0" data-conduct-case={c.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.employee_name}</div>
                    <div className="max-w-2xl text-[12px] text-brand-grey">{c.allegation}</div>
                    <div className="text-[11px] text-brand-grey">
                      {c.category} · {c.stage} · {t("cdt.opened", { date: c.opened_on ?? "" })}
                    </div>
                    {c.faults.length > 0 && (
                      <div className="text-[11px] text-status-red" data-conduct-case-faults={c.name}>
                        {c.faults.map((f) => t(`cdt.fault.${f}`)).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {c.outcome ? (
                      <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${OUTCOME_CLS[c.outcome] ?? ""}`}>
                        {c.outcome}
                      </span>
                    ) : (
                      <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-status-amber">
                        {t("cdt.stillOpen")}
                      </span>
                    )}
                    {c.replied_on && <div className="text-[11px] text-brand-grey">{t("cdt.replied", { date: c.replied_on })}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={t("cdt.evidence")} aside={t("cdt.evidenceHint")}>
        <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-5" data-conduct-evidence>
          {d.evidence.map((e) => (
            <div key={e.key}>
              <div className="font-heading text-xl font-semibold">{e.value}</div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`cdt.fact.${e.key}`)}</div>
            </div>
          ))}
        </div>
        <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey">
          {t("cdt.evidenceNote")}
        </p>
      </SectionCard>

      {openChannel.isError && <ErrorBox error={openChannel.error} />}
    </div>
  );
}
